import React from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Globe, MessageSquareText, LogOut, BarChart3, Inbox, Languages, Puzzle, Settings as SettingsIcon, CreditCard, GraduationCap } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { ExtensionDialog } from "@/components/ExtensionDialog";

export function Topbar() {
  const { user, logout } = useAuth();
  const { t, lang, toggle } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const isBusiness = user && (user.role === "manager" || user.role === "agent");
  const isManager = user && user.role === "manager";

  const navItems = [
    { to: "/app", label: t("translator"), icon: MessageSquareText, show: true },
    { to: "/inbox", label: t("inbox"), icon: Inbox, show: isBusiness },
    { to: "/learning", label: t("learning"), icon: GraduationCap, show: isBusiness },
    { to: "/analytics", label: t("analytics"), icon: BarChart3, show: isBusiness },
    { to: "/settings", label: t("settings"), icon: SettingsIcon, show: isBusiness },
    { to: "/plans", label: t("plans"), icon: CreditCard, show: true },
  ].filter((i) => i.show);

  return (
    <header className="sticky top-0 z-30 backdrop-blur-xl bg-white/85 border-b border-border">
      <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between gap-4">
        <Link to="/app" className="flex items-center gap-2" data-testid="topbar-logo">
          <div className="h-9 w-9 rounded-xl bg-primary grid place-items-center text-white">
            <Languages size={20} />
          </div>
          <span className="font-head font-bold text-lg text-primary">{t("appName")}</span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {navItems.map(({ to, label, icon: Icon }) => (
            <Link
              key={to}
              to={to}
              data-testid={`nav-${to.replace("/", "")}`}
              className={`flex items-center gap-2 px-4 h-10 rounded-full text-sm font-medium transition-colors ${
                location.pathname === to
                  ? "bg-secondary text-primary"
                  : "text-muted-foreground hover:bg-secondary/60"
              }`}
            >
              <Icon size={17} /> {label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <ExtensionDialog
            trigger={
              <button
                data-testid="get-extension-btn"
                className="hidden sm:flex items-center gap-1.5 h-10 px-3 rounded-full bg-primary/5 text-primary border border-primary/15 text-sm font-medium hover:bg-primary hover:text-white transition-colors"
              >
                <Puzzle size={16} /> {lang === "en" ? "Get Extension" : "إضافة المتصفح"}
              </button>
            }
          />
          <button
            onClick={toggle}
            data-testid="lang-toggle"
            className="flex items-center gap-1.5 h-10 px-3 rounded-full border border-border text-sm font-medium hover:bg-secondary transition-colors"
          >
            <Globe size={16} /> {lang === "en" ? "العربية" : "EN"}
          </button>
          {user && (
            <Button
              variant="ghost"
              size="icon"
              onClick={async () => { await logout(); navigate("/login"); }}
              data-testid="logout-btn"
              title={t("logout")}
            >
              <LogOut size={18} />
            </Button>
          )}
        </div>
      </div>
      <nav className="md:hidden flex items-center gap-1 px-3 pb-2 overflow-x-auto">
        {navItems.map(({ to, label, icon: Icon }) => (
          <Link
            key={to}
            to={to}
            className={`flex items-center gap-1.5 px-3 h-9 rounded-full text-sm whitespace-nowrap ${
              location.pathname === to ? "bg-secondary text-primary" : "text-muted-foreground"
            }`}
          >
            <Icon size={15} /> {label}
          </Link>
        ))}
      </nav>
    </header>
  );
}
